import { Component, TemplateFunction } from '@ribajs/core'
import type {
  RestockSubscribeResponse,
  ShopifyProductVariant,
  ShopifyProduct,
} from '../types/index.js'

/**
 * The type for the scope
 */
interface Scope {
  // Properties

  variant?: ShopifyProductVariant
  product?: ShopifyProduct

  feedback: {
    success: boolean
    successMessage: string
    error: boolean
    errorMessage: string
  }
  showForm: boolean
  loading: boolean
  email?: string
  marketing: boolean

  // Methods

  onSubmit: RestockNotificationComponent['onSubmit']

  // Elements
  formEl?: HTMLFormElement
}

/**
 * A custom component for the Shopify App "SC Back in Stock Restock Alert"
 */
export class RestockNotificationComponent extends Component {
  public static tagName = 'restock-notification'

  public scope: Scope = {
    // Properties

    variant: undefined,

    feedback: {
      success: false,
      successMessage: '',
      error: false,
      errorMessage: '',
    },
    showForm: true,
    loading: false,
    email: '',
    marketing: false,

    // Methods

    onSubmit: this.onSubmit,
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return [
      'success-message-template',
      'error-message-template',
      'variant',
      'product',
      'email',
      'marketing',
    ]
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(RestockNotificationComponent.observedAttributes)
  }

  // deconstruction
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  async afterBind() {
    await super.afterBind()
    this.classList.add(`${RestockNotificationComponent.tagName}-ready`)
    this.addEventListeners()
  }

  protected addEventListeners() {
    // Nothing yet
  }

  protected removeEventListeners() {
    // Nothing yet
  }

  /** Called on submit, see `rv-on-submit="onSubmit"` in the template */
  public async onSubmit(event: Event, scope: Scope, form: HTMLFormElement) {
    // If the validation fails, return true and the form is processed as standard
    if (this.scope.formEl?.checkValidity() === false) {
      return true
    }

    event?.preventDefault()

    this.scope.loading = true

    try {
      const formData = new FormData(form) as any
      formData.set('marketing', this.scope.marketing)
      const formDataObject = Object.fromEntries(formData.entries())
      // Format the plain form data as JSON
      const formDataJsonString = JSON.stringify(formDataObject)

      const response = await fetch(form.action, {
        method: form.method,
        headers: {
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json;charset=UTF-8',
        },
        body: formDataJsonString,
      })

      const result: RestockSubscribeResponse = await response.json()
      this.scope.loading = false

      this.scope.showForm = false
      if (result.success) {
        this.scope.feedback.success = true
        this.scope.feedback.successMessage = result.message || ''
      } else if (result.message && response.status == 200) {
        this.scope.feedback.success = true
        this.scope.feedback.successMessage = result.message || ''
      } else {
        this.scope.feedback.error = true
        this.scope.feedback.errorMessage = result.message || ''
      }
    } catch (error: any) {
      console.warn('error', error)
      this.scope.loading = false
      this.scope.feedback.error = true
      this.scope.feedback.errorMessage = error?.message || ''
    }
  }

  /**
   * Returns the template, if null is returned, the child html of this element / component is not replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
